import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";
import ihelmPlatform from "@/assets/ihelm-solutions.png";
import cetafuelVisual from "@/assets/cetafuel-visual.png";

const Solutions = () => {
  
  const { t, i18n } = useTranslation('solutions');
  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <html lang={i18n.language} />
        <title>{t('meta.title')}</title>
        <meta name="description" content={t('meta.description')} />
        <link rel="canonical" href="https://www.cetasol.com/solutions" />
        <meta property="og:title" content={t('meta.title')} />
        <meta property="og:description" content={t('meta.description')} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.cetasol.com/solutions" />
        <meta property="og:image" content="https://www.cetasol.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t('meta.title')} />
        <meta name="twitter:description" content={t('meta.description')} />
        <meta name="twitter:image" content="https://www.cetasol.com/og-image.jpg" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.cetasol.com/" },
            { "@type": "ListItem", "position": 2, "name": "Solutions", "item": "https://www.cetasol.com/solutions" }
          ]
        })}</script>
      </Helmet>
      <Header />
      <main className="pt-24 pb-20">
        <div className="container mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-2xl mb-16">
            <p className="text-xs font-semibold tracking-widest uppercase text-section-label mb-4">{t('label')}</p>
            <h1 className="font-display text-4xl md:text-5xl font-bold text-foreground mb-6">{t('heading')}</h1>
            <p className="text-lg text-muted-foreground leading-relaxed">{t('description')}</p>
          </motion.div>
        </div>

        <section className="bg-surface-light py-20">
          <div className="container mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
              <p className="text-xs font-semibold tracking-widest uppercase text-foreground mb-4">{t('ihelmLabel')}</p>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">iHelm</h2>
              <p className="text-muted-foreground leading-relaxed mb-6">{t('ihelmDescription')}</p>
              <Link to="/ihelm-intelligent-platform" className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:gap-3 transition-all">{t('ihelmCta')} <ArrowRight size={16} /></Link>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.15 }}>
              <img src={ihelmPlatform} alt="iHelm platform" className="w-full " loading="lazy" />
            </motion.div>
          </div>
        </section>

        <section className="py-20 section-fade">
          <div className="container mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.15 }} className="order-2 md:order-1">
              <img src={cetafuelVisual} alt="CetaFuel virtual fuel sensor technology" className="w-full " loading="lazy" />
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="order-1 md:order-2">
              <p className="text-xs font-semibold tracking-widest uppercase text-section-label mb-4">{t('cetafuelLabel')}</p>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">CetaFuel</h2>
              <p className="text-muted-foreground leading-relaxed mb-6">{t('cetafuelDescription')}</p>
              <Link to="/cetafuel" className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:gap-3 transition-all">{t('cetafuelCta')} <ArrowRight size={16} /></Link>
            </motion.div>
          </div>
        </section>

        <section className="bg-surface-light py-20">
          <motion.div className="container mx-auto px-6" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
            <p className="text-sm text-muted-foreground text-center mb-4">{t('segmentIntro')}</p>
            <div className="flex flex-wrap justify-center gap-3">
              {["Ferry","Tug","Merchant","CTV","Pilot","Fishing"].map((seg) => (
                <Link key={seg} to={`/segments/${seg.toLowerCase()}`} className="px-4 py-2 rounded-md text-sm font-medium border border-border bg-card text-muted-foreground hover:border-primary/50 hover:text-foreground transition-colors">{seg}</Link>
              ))}
            </div>
          </motion.div>
        </section>

        <section className="container mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="text-center py-[63px]">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-4">{t('ctaHeading')}</h2>
            <p className="text-muted-foreground mb-8 max-w-lg mx-auto">{t('ctaDescription')}</p>
            <Link to="/book-a-demo" className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-3.5 rounded-md font-medium text-sm hover:bg-primary-hover transition-colors">{t('ctaButton')} <ArrowRight size={16} /></Link>
          </motion.div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Solutions;
